import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import Navbar from '../components/Navbar';

interface TeamMember {
  id: string;
  name: string;
  miles: number;
  joinedAt: string;
}

const TeamView = () => {
  const { currentUser, userDetails } = useAuth();
  
  // Team state
  const [teamName, setTeamName] = useState<string>('');
  const [teamProvince, setTeamProvince] = useState<string>('');
  const [teamRank, setTeamRank] = useState<number | null>(null);
  const [teamTotalMiles, setTeamTotalMiles] = useState<number>(0);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamCreator, setTeamCreator] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>('');
  
  // Loading state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!currentUser || !userDetails || !userDetails.teamId) {
        setError("You are not part of any team");
        setLoading(false);
        return;
      }
      
      try {
        // Get team details
        const teamDoc = await getDoc(doc(db, 'teams', userDetails.teamId));
        
        if (!teamDoc.exists()) {
          setError("Team not found");
          setLoading(false);
          return;
        }
        
        const teamData = teamDoc.data();
        setTeamName(teamData.name);
        setTeamProvince(teamData.province);
        setTeamTotalMiles(teamData.totalMiles || 0);
        setTeamCreator(teamData.createdBy);
        setCreatedAt(teamData.createdAt);
        
        // Get team rank
        const teamsRef = collection(db, 'teams');
        const teamRankQuery = query(
          teamsRef,
          orderBy('totalMiles', 'desc')
        );
        const teamRankSnapshot = await getDocs(teamRankQuery);
        
        let rank = 1;
        teamRankSnapshot.forEach((doc) => {
          if (doc.id === userDetails.teamId) {
            setTeamRank(rank);
          }
          rank++;
        });
        
        // Get team members
        const usersRef = collection(db, 'users');
        const teamMembersQuery = query(
          usersRef,
          where('teamId', '==', userDetails.teamId)
        );
        const teamMembersSnapshot = await getDocs(teamMembersQuery);
        
        const members: TeamMember[] = [];
        
        // First, collect user data
        const memberPromises = teamMembersSnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          
          // Get user miles
          const milesRef = collection(db, 'miles');
          const userMilesQuery = query(
            milesRef,
            where('userId', '==', userDoc.id)
          );
          const milesSnapshot = await getDocs(userMilesQuery);
          
          let totalMiles = 0;
          milesSnapshot.forEach((doc) => {
            const milesData = doc.data();
            totalMiles += Number(milesData.miles);
          });
          
          members.push({
            id: userDoc.id,
            name: `${userData.firstName} ${userData.lastName}`,
            miles: totalMiles,
            joinedAt: userData.createdAt
          });
        });
        
        await Promise.all(memberPromises);
        
        // Sort members by miles
        members.sort((a, b) => b.miles - a.miles);
        setTeamMembers(members);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching team data:', error);
        setError('Could not load team information');
        setLoading(false);
      }
    };
    
    fetchTeamData();
  }, [currentUser, userDetails]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-primary py-4 px-6">
              <h2 className="text-xl font-semibold text-white">Team</h2>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <p className="text-lg text-gray-700 mb-4">{error}</p>
                <p className="text-gray-500">
                  You are not currently part of any team. You can join a team during registration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Team Overview */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="bg-primary py-4 px-6">
            <h2 className="text-xl font-semibold text-white">Team: {teamName}</h2>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-500">Total Team Miles</p>
                <p className="text-3xl font-bold text-primary">{teamTotalMiles.toFixed(1)}</p>
                {teamRank !== null && (
                  <p className="text-sm text-gray-500 mt-1">Rank: #{teamRank}</p>
                )}
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-500">Team Information</p>
                <p className="font-medium text-gray-800 mt-1">
                  Province: <span className="font-bold">{teamProvince}</span>
                </p>
                <p className="font-medium text-gray-800">
                  Members: <span className="font-bold">{teamMembers.length}</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {new Date(createdAt).toLocaleDateString()}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-500">Average per Member</p>
                <p className="text-3xl font-bold text-secondary">
                  {teamMembers.length > 0 
                    ? (teamTotalMiles / teamMembers.length).toFixed(1) 
                    : '0.0'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Based on {teamMembers.length} members
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Team Members */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-50 py-3 px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
          </div>
          
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Miles
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % of Team Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMembers.map((member) => (
                    <tr key={member.id} className={currentUser?.uid === member.id ? 'bg-primary bg-opacity-5' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {member.name}
                              {currentUser?.uid === member.id && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                                  You
                                </span>
                              )}
                              {member.id === teamCreator && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Team Creator
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.miles.toFixed(1)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {teamTotalMiles > 0 
                            ? ((member.miles / teamTotalMiles) * 100).toFixed(1) 
                            : '0.0'}%
                        </div>
                        <div className="mt-1 w-24 bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-primary h-2.5 rounded-full" 
                            style={{ width: `${Math.min(100, teamTotalMiles > 0 ? (member.miles / teamTotalMiles) * 100 : 0)}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamView; 